import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

interface State {
    blur: number;
    opacity: number;
    saturation: number;
    bgColor: string;
    borderColor: string;
    borderWidth: number;
    radius: number;
    bgImage: string;
    copied: boolean;
}

const SAMPLE_BG = 'linear-gradient(135deg, #6366f1 0%, #ec4899 50%, #f59e0b 100%)';

export default class GlassmorphismGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        blur: 12,
        opacity: 0.25,
        saturation: 180,
        bgColor: '#ffffff',
        borderColor: '#ffffff',
        borderWidth: 1,
        radius: 16,
        bgImage: SAMPLE_BG,
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get cssCode(): string {
        const { blur, opacity, saturation, bgColor, borderColor, borderWidth, radius } = this.state;
        const rgba = (() => {
            const m = bgColor.match(/^#?([0-9a-f]{6}|[0-9a-f]{3})$/i);
            if (!m) return `rgba(255, 255, 255, ${opacity})`;
            let h = m[1];
            if (h.length === 3) h = h.split('').map(c => c + c).join('');
            const v = parseInt(h, 16);
            return `rgba(${(v >> 16) & 0xff}, ${(v >> 8) & 0xff}, ${v & 0xff}, ${opacity})`;
        })();
        return `background: ${rgba};\nbackdrop-filter: blur(${blur}px) saturate(${saturation}%);\n-webkit-backdrop-filter: blur(${blur}px) saturate(${saturation}%);\nborder: ${borderWidth}px solid ${borderColor}40;\nborder-radius: ${radius}px;`;
    }

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { blur, opacity, saturation, bgColor, borderColor, borderWidth, radius, bgImage, copied } = this.state;
        const rgba = (() => {
            const m = bgColor.match(/^#?([0-9a-f]{6}|[0-9a-f]{3})$/i);
            if (!m) return `rgba(255, 255, 255, ${opacity})`;
            let h = m[1];
            if (h.length === 3) h = h.split('').map(c => c + c).join('');
            const v = parseInt(h, 16);
            return `rgba(${(v >> 16) & 0xff}, ${(v >> 8) & 0xff}, ${v & 0xff}, ${opacity})`;
        })();
        const previewStyle: React.CSSProperties = {
            background: rgba,
            backdropFilter: `blur(${blur}px) saturate(${saturation}%)`,
            WebkitBackdropFilter: `blur(${blur}px) saturate(${saturation}%)`,
            border: `${borderWidth}px solid ${borderColor}40`,
            borderRadius: `${radius}px`,
            padding: '2rem 3rem',
            color: '#fff',
            fontWeight: 600,
            fontSize: '1.1rem',
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        };

        const slider = (label: string, value: number, min: number, max: number, step: number, fmt: (v: number) => string, onChange: (v: number) => void) => (
            <div className={genStyles.field}>
                <label>{label}: <strong>{fmt(value)}</strong></label>
                <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(+e.target.value)} />
            </div>
        );

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Glass effect</span></div>
                    {slider('Blur', blur, 0, 40, 1, v => `${v}px`, v => this.setState({ blur: v }))}
                    {slider('Opacity', opacity, 0, 1, 0.01, v => v.toFixed(2), v => this.setState({ opacity: v }))}
                    {slider('Saturation', saturation, 50, 300, 1, v => `${v}%`, v => this.setState({ saturation: v }))}
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Border &amp; shape</span></div>
                    {slider('Border', borderWidth, 0, 6, 1, v => `${v}px`, v => this.setState({ borderWidth: v }))}
                    {slider('Radius', radius, 0, 50, 1, v => `${v}px`, v => this.setState({ radius: v }))}
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Colors</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="gm-tint">Tint</label>
                            <input id="gm-tint" type="color" value={bgColor} style={{ height: 38, padding: 4 }}
                                onChange={e => this.setState({ bgColor: e.target.value })} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="gm-border">Border</label>
                            <input id="gm-border" type="color" value={borderColor} style={{ height: 38, padding: 4 }}
                                onChange={e => this.setState({ borderColor: e.target.value })} />
                        </div>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="gm-bg">Background CSS (surface behind glass)</label>
                        <input id="gm-bg" type="text" value={bgImage}
                            onChange={e => this.setState({ bgImage: e.target.value })} />
                    </div>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                preview={
                    <div style={{ background: bgImage, padding: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 280 }}>
                        <div style={previewStyle}>Glass card</div>
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
